
import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import QRScanner from './QRScanner';
import { appConfig } from '../config';
import './LoginScreen.css';

const LoginScreen = () => {
  const [pin, setPin] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [isQRScannerOpen, setIsQRScannerOpen] = useState(false);
  const [lockoutSeconds, setLockoutSeconds] = useState(0);
  const [forcedPinModal, setForcedPinModal] = useState(null); // { pin, qrToken }
  const lockoutTimer = React.useRef(null);
  const { login, error, clearError } = useAuth();
  const { t } = useLanguage();

  // Lockout-Countdown
  React.useEffect(() => {
    if (lockoutSeconds > 0) {
      lockoutTimer.current = setTimeout(() => setLockoutSeconds(s => s - 1), 1000);
    }
    return () => clearTimeout(lockoutTimer.current);
  }, [lockoutSeconds]);

  const startLockout = (seconds) => setLockoutSeconds(seconds);

  // Check for PIN in URL query parameters on component mount
  useEffect(() => {
    const urlParams   = new URLSearchParams(window.location.search);
    const pinFromUrl  = urlParams.get('pin');
    const tokenFromUrl = urlParams.get('t');   // QR-Code-Login-Token

    if (pinFromUrl) {
      setPin(pinFromUrl);
      // PIN aus der URL entfernen, damit er nicht im Browser-Verlauf bleibt
      const cleanUrl = window.location.pathname + window.location.hash;
      window.history.replaceState({}, '', cleanUrl);
      // Automatically attempt login with PIN from URL
      handleAutoLogin(pinFromUrl);
    } else if (tokenFromUrl) {
      // QR-Token aus URL entfernen — darf nicht im Browser-Verlauf landen
      const cleanUrl = window.location.pathname + window.location.hash;
      window.history.replaceState({}, '', cleanUrl);
      handleQrTokenLogin(tokenFromUrl);
    }
  }, []); // Empty dependency array since handlers are defined in component

  /**
   * Login via QR-Token (POST /api/login/qr).
   * Speichert Tokens mit denselben localStorage-Keys wie AuthContext,
   * damit PublicRoute den Login erkennt und weiterleitet.
   */
  const handleQrTokenLogin = async (qrToken) => {
    setIsLoading(true);
    setMessage({ text: '', type: '' });
    try {
      const { appConfig } = await import('../config');
      const res = await fetch(`${appConfig.apiBaseUrl}/login/qr`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ qr_token: qrToken }),
      });
      const data = await res.json();

      if (res.ok && data.access_token) {
        // Tokens exakt so speichern wie AuthContext es erwartet
        localStorage.setItem('hejvi_access_token',  data.access_token);
        localStorage.setItem('hejvi_refresh_token', data.refresh_token);
        localStorage.setItem('hejvi_token_expiry',  String(Date.now() + 15 * 60 * 1000));
        if (data.user) localStorage.setItem('hejvi_user', JSON.stringify(data.user));

        if (data.forced_new_pin) {
          setForcedPinModal({
            pin:     data.forced_new_pin,
            qrToken: data.forced_new_qr_token || null,
          });
        } else {
          window.location.href = '/';
        }
      } else {
        const errCode = data.error || '';
        if (errCode === 'too_many_attempts') {
          const wait = Number(data.retry_after) || 60;
          startLockout(wait);
          setMessage({ text: t('tooManyAttempts') || 'Zu viele Versuche — bitte kurz warten.', type: 'error' });
        } else {
          setMessage({ text: t('wrongPin') || 'Ungültiger QR-Code.', type: 'error' });
        }
      }
    } catch (err) {
      setMessage({ text: t('networkError') || 'Netzwerkfehler.', type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Führt den Login mit PIN durch.
   * Ruft die API direkt auf (wie handleQrTokenLogin), damit forced_new_pin
   * aus der Antwort verarbeitet werden kann, ohne AuthContext zu ändern.
   */
  const handleAutoLogin = async (pinValue) => {
    setIsLoading(true);
    setMessage({ text: '', type: '' });

    try {
      const res = await fetch(`${appConfig.apiBaseUrl}/login`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ pin: pinValue }),
      });
      const data = await res.json();

      if (res.ok && data.access_token) {
        // Tokens speichern — identisch zu AuthContext-Keys
        localStorage.setItem('hejvi_access_token',  data.access_token);
        localStorage.setItem('hejvi_refresh_token', data.refresh_token);
        localStorage.setItem('hejvi_token_expiry',  String(Date.now() + 15 * 60 * 1000));
        if (data.user) localStorage.setItem('hejvi_user', JSON.stringify(data.user));

        // forced_new_pin: System hat beim Login neuen PIN generiert (must_change_pin-Flag).
        // Nutzer sieht seinen neuen PIN einmalig — Admin kennt ihn nicht.
        if (data.forced_new_pin) {
          setForcedPinModal({
            pin:     data.forced_new_pin,
            qrToken: data.forced_new_qr_token || null,
          });
        } else {
          window.location.href = '/';
        }
      } else {
        const errCode = data.error || '';
        if (errCode === 'too_many_attempts') {
          const wait = Number(data.retry_after) || 60;
          startLockout(wait);
          setMessage({ text: t('tooManyAttempts') || 'Zu viele Fehlversuche – bitte warte kurz.', type: 'error' });
        } else if (errCode === 'wrong_pin') {
          setMessage({ text: t('wrongPin'), type: 'error' });
        } else if (errCode === 'pin_conflict') {
          setMessage({ text: 'Systemfehler: PIN-Konflikt. Bitte Support kontaktieren.', type: 'error' });
        } else {
          const lower = (errCode || '').toLowerCase();
          if (lower.includes('connect') || lower.includes('server')) {
            setMessage({ text: t('networkError'), type: 'error' });
          } else {
            setMessage({ text: errCode || t('generalError'), type: 'error' });
          }
        }
      }
    } catch (err) {
      setMessage({ text: t('networkError') || 'Netzwerkfehler.', type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  // AuthContext-Fehler-Sync entfällt — handleAutoLogin ruft API direkt auf
  // und verarbeitet Fehler selbst. clearError() bleibt für Edge-Cases erhalten.

  const validatePin = (value) => {
    return value; // allow any characters, no length limit
  };

  const handlePinChange = (e) => {
    const validatedPin = validatePin(e.target.value);
    setPin(validatedPin);
    
    if (message.text) {
      setMessage({ text: '', type: '' });
      clearError();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    await handleAutoLogin(pin);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSubmit(e);
    }
  };

  const handleQRScan = async (data) => {
    try {
      const trimmed = (data || '').trim();
      console.log('QR Code scanned:', trimmed);
      
      // Check if it's a login URL with PIN - support multiple URL patterns
      // More flexible approach to extract PIN from any URL
      
      let pinFromQR = null;
      let tokenFromQR = null;

      // Pattern 0: QR-Code enthält Login-Token (?t=xxx) — neuer Token-basierter Login
      const tokenPattern = /[?&]t=([a-f0-9]{32})(?:&|$)/i;
      const tokenMatch = trimmed.match(tokenPattern);
      if (tokenMatch && tokenMatch[1]) {
        tokenFromQR = tokenMatch[1];
        console.log('QR-Login-Token erkannt:', tokenFromQR);
      }

      if (tokenFromQR) {
        setIsQRScannerOpen(false);
        await handleQrTokenLogin(tokenFromQR);
        return;
      }

      // Fallback: PIN-basierter QR-Code (altes Format / manuell generiert)
      // Pattern 1: Any URL with pin parameter
      const anyUrlWithPinPattern = /[?&]pin=([^&\s]+)/i;
      // Pattern 2: Just the PIN value (fallback for simple QR codes)
      const pinOnlyPattern = /^[a-zA-Z0-9_-]+$/;

      // Try to find pin parameter in any URL
      const match = trimmed.match(anyUrlWithPinPattern);
      if (match && match[1]) {
        pinFromQR = match[1];
        console.log('PIN extracted from URL parameter:', pinFromQR);
      } else if (pinOnlyPattern.test(trimmed)) {
        // If it's just a PIN value, use it directly
        pinFromQR = trimmed;
        console.log('Using direct PIN value:', pinFromQR);
      } else {
        // Try to extract PIN from the last part of the URL path
        const urlPathMatch = trimmed.match(/\/([a-zA-Z0-9_-]+)(?:\?|$)/i);
        if (urlPathMatch && urlPathMatch[1] && pinOnlyPattern.test(urlPathMatch[1])) {
          pinFromQR = urlPathMatch[1];
          console.log('PIN extracted from URL path:', pinFromQR);
        }
      }
      
      if (pinFromQR) {
        console.log('PIN extracted from QR:', pinFromQR);
        setPin(pinFromQR);
        setIsQRScannerOpen(false);
        // Automatically attempt login with PIN from QR
        await handleAutoLogin(pinFromQR);
      } else {
        console.log('No valid PIN found in QR code');
        setMessage({ text: 'Invalid login QR code - no PIN found', type: 'error' });
        setIsQRScannerOpen(false);
      }
    } catch (error) {
      console.error('Error processing QR code:', error);
      setMessage({ text: 'Failed to process QR code', type: 'error' });
      setIsQRScannerOpen(false);
    }
  };

  return (
    <>
      <div className="main-content">
        {/* Logo Section */}
        <div className="logo-section">
          <h1 className="logo">HeyVi</h1>
        </div>

        {/* Login Form */}
        <div className="login-section">
          <div className="login-header">
            <h2 className="login-title">{t('login')}</h2>
            <p className="welcome-text">{t('welcome')}</p>
          </div>

          <form className="form-container" onSubmit={handleSubmit}>
            <div className="input-group">
              <label className="input-label" htmlFor="pin-input">
                <svg className="lock-icon" width="11" height="12" viewBox="0 0 11 12" fill="none">
                  <path d="M2.75 5.5V3.5C2.75 1.84 4.09 0.5 5.75 0.5C7.41 0.5 8.75 1.84 8.75 3.5V5.5H9.5C10.05 5.5 10.5 5.95 10.5 6.5V10.5C10.5 11.05 10.05 11.5 9.5 11.5H2C1.45 11.5 1 11.05 1 10.5V6.5C1 5.95 1.45 5.5 2 5.5H2.75ZM4 5.5H7.5V3.5C7.5 2.4 6.6 1.5 5.5 1.5C4.4 1.5 3.5 2.4 3.5 3.5V5.5H4Z" fill="#FB923C"/>
                </svg>
                {t('enterPin')}
              </label>
              <input 
                type="password" 
                id="pin-input"
                className={`pin-input ${message.type === 'error' ? 'error' : ''}`}
                placeholder=""
                value={pin}
                onChange={handlePinChange}
                onKeyDown={handleKeyDown}
                // no maxLength restriction
                autoComplete="off"
                disabled={isLoading}
              />
            </div>

            <button 
              type="submit" 
              className={`login-button ${isLoading ? 'loading' : ''} ${lockoutSeconds > 0 ? 'locked' : ''}`}
              disabled={isLoading || pin.length === 0 || lockoutSeconds > 0}
            >
              {!isLoading && lockoutSeconds <= 0 && (
                <>
                  <svg className="login-icon" width="18" height="16" viewBox="0 0 18 16" fill="none">
                    <path d="M10 1L16 8L10 15M16 8H2" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <span>{t('login')}</span>
                </>
              )}
              {lockoutSeconds > 0 && (
                <span>🔒 {lockoutSeconds}s</span>
              )}
            </button>

            {/* Message Display */}
            {message.text && (
              <div className={`message ${message.type}`}>
                {message.text}
                {lockoutSeconds > 0 && (
                  <span className="lockout-countdown"> ({lockoutSeconds}s)</span>
                )}
              </div>
            )}
          </form>

          {/* QR Login Option */}
          <div className="qr-login-section">
            <button 
              type="button"
              className="qr-login-button"
              onClick={() => setIsQRScannerOpen(true)}
              disabled={isLoading}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M3 3H11V11H3V3Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M13 3H21V11H13V3Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M3 13H11V21H3V13Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M13 13H15V15H13V13Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M17 13H19V15H17V13Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M13 17H15V19H13V17Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M17 17H19V19H17V17Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M17 21H21V19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M21 15H19V17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              {t('loginWithQRCode') || 'Login with QR Code'}
            </button>
          </div>
        </div>
      </div>

      {/* Erzwungener PIN-Wechsel: System hat neuen PIN generiert, User sieht ihn einmalig */}
      {forcedPinModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 1000, padding: 20,
        }}>
          <div style={{
            background: '#fff', borderRadius: 16, padding: 32, maxWidth: 420, width: '100%',
            boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
          }}>
            <div style={{ textAlign: 'center', marginBottom: 24 }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>🔐</div>
              <h2 style={{ fontSize: 20, fontWeight: 700, margin: '0 0 8px' }}>
                Dein persönlicher PIN
              </h2>
              <p style={{ fontSize: 13, color: '#6B7280', margin: 0, lineHeight: 1.5 }}>
                Aus Datenschutzgründen wurde für dich automatisch ein neuer PIN generiert.
                Nur du siehst ihn — bitte jetzt notieren oder sichern.
              </p>
            </div>

            {/* PIN-Anzeige */}
            <div style={{
              background: '#fefce8', border: '2px solid #f59e0b', borderRadius: 12,
              padding: '16px 20px', marginBottom: 20, textAlign: 'center',
            }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#92400e', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 8 }}>
                Neuer PIN (8 Zeichen)
              </div>
              <div style={{ fontFamily: 'monospace', fontSize: 28, fontWeight: 800, letterSpacing: '.18em', color: '#1c1917', marginBottom: 12 }}>
                {forcedPinModal.pin}
              </div>
              <button
                onClick={() => navigator.clipboard.writeText(forcedPinModal.pin).catch(() => {})}
                style={{ background: '#f59e0b', color: '#fff', border: 'none', borderRadius: 7,
                  padding: '7px 18px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}
              >
                📋 PIN kopieren
              </button>
            </div>

            <p style={{ fontSize: 11, color: '#9CA3AF', textAlign: 'center', marginBottom: 20, lineHeight: 1.5 }}>
              Dieser PIN wird dir nur einmal angezeigt.<br/>
              Dein QR-Code zum Scannen ist ebenfalls aktualisiert.
            </p>

            <button
              style={{
                width: '100%', background: '#FF6407', color: '#fff', border: 'none',
                borderRadius: 10, padding: '12px 0', fontSize: 15, fontWeight: 700,
                cursor: 'pointer',
              }}
              onClick={() => { setForcedPinModal(null); window.location.href = '/'; }}
            >
              ✓ PIN notiert — zur App
            </button>
          </div>
        </div>
      )}

      {/* QR Scanner Modal */}
      <QRScanner
        isOpen={isQRScannerOpen}
        onClose={() => setIsQRScannerOpen(false)}
        onScan={handleQRScan}
      />
    </>
  );
};

export default LoginScreen;